import { useLocation, useSearchParams } from 'react-router-dom';
import { X } from '@phosphor-icons/react';
import { useNotification } from '../../../contexts/NotificationContext';
import { useDemoMode } from '../../../contexts/DemoModeContext';
import { alsModulListe, routeItemId, type VisibilityContext } from '../../../lib/sidebar/navigation';
import { dienstleisterIdAusPfad, eigentuemerIdAusPfad, mieterIdAusPfad, objektOrtAusPfad, sonstigerKontaktIdAusPfad } from '../../../lib/immobilien/sidebarNav';
import { kontaktTab } from '../../../lib/kontakte/detailTab';
import { useSonstigerKontakt } from '../../../lib/kontakte/useSonstigerKontakt';
import { useMieterKopf } from '../../../lib/mieter/useMieterKopf';
import { useEigentuemerKopf } from '../../../lib/eigentuemer/useEigentuemerKopf';
import { useDienstleisterKopf } from '../../../lib/dienstleister/useDienstleisterKopf';
import { useObjektKontext } from '../../../lib/immobilien/useObjektKontext';
import { useIslandActions } from '../../island/IslandProvider';
import { useSidebarDaten } from '../../admin/useSidebarDaten';
import { Chip } from '../ui/Chip';
import { ListenModus } from './ListenModus';
import { ObjektModus } from './ObjektModus';
import { MieterModus } from './MieterModus';
import { EigentuemerModus } from './EigentuemerModus';
import { DienstleisterModus } from './DienstleisterModus';
import { KontaktModus } from './KontaktModus';
import { EmailModus, EmailModusKopf } from './EmailModus';
import { Nutzerkarte } from './Nutzerkarte';

/**
 * Kontext-Sidebar des Immobilienbereichs (README §5.6).
 * Listenmodus auf /admin/objekte, Objektmodus auf /admin/objekte/:id…, Mietermodus auf /admin/mieter/:id, Eigentümermodus auf /admin/eigentuemer/:id
 * Ab lg sticky (236 px), darunter Schublade wie die klassische Sidebar.
 */
export interface ImmoSidebarProps {
  onNavigate: (pfad: string) => void;
  onLogout: () => void;
  userEmail?: string;
  userRole?: string;
  avatarUrl?: string;
  fullName?: string;
  allowedModules?: unknown;
  isOpen: boolean;
  onClose: () => void;
}

function rollenLabel(rolle?: string): string {
  const r = (rolle ?? '').toLowerCase();
  if (r === 'super admin') return 'Super Admin';
  if (r === 'admin') return 'Admin';
  if (r === 'team') return 'Team';
  return rolle || 'User';
}

export function ImmoSidebar({ onNavigate, onLogout, userEmail, userRole, avatarUrl, fullName, allowedModules, isOpen, onClose }: ImmoSidebarProps) {
  const location = useLocation();
  const [suchParameter] = useSearchParams();
  const { activeModules, loading, rechnungenBadge, emailBadge, vorschlaegeBadge } = useSidebarDaten();
  const { unreadByModul } = useNotification();
  const { isDemoMode } = useDemoMode();
  const { openSearch } = useIslandActions();

  const ort = objektOrtAusPfad(location.pathname);
  const { zustand } = useObjektKontext(ort?.objektId ?? null);
  const mieterId = mieterIdAusPfad(location.pathname);
  const mieterZustand = useMieterKopf(mieterId);
  const eigentuemerId = eigentuemerIdAusPfad(location.pathname);
  const eigentuemerZustand = useEigentuemerKopf(eigentuemerId);
  const dienstleisterId = dienstleisterIdAusPfad(location.pathname);
  const dienstleisterZustand = useDienstleisterKopf(dienstleisterId);
  const kontaktId = sonstigerKontaktIdAusPfad(location.pathname);
  const kontakt = useSonstigerKontakt(kontaktId);
  const emailBereich = location.pathname === '/admin/emails' || location.pathname.startsWith('/admin/emails/');

  const rolle = (userRole ?? '').toLowerCase();
  const sichtbarkeit: VisibilityContext = {
    isSuperAdmin: rolle === 'super admin',
    isTeam: rolle === 'team',
    activeModules,
    allowedModules: alsModulListe(allowedModules),
  };

  return (
    <aside
      aria-label="Seitenleiste"
      className={`fixed lg:sticky top-0 left-0 z-[110] lg:z-auto shrink-0 transition-transform duration-300 lg:translate-x-0 ${isOpen ? 'translate-x-0' : '-translate-x-full'}`}
      style={{ width: 236, height: '100dvh', padding: '14px 0 14px 14px', paddingTop: 'max(14px, env(safe-area-inset-top))' }}
    >
      <div className="immo-glas immo-sidebar-innen flex flex-col h-full" style={{ borderRadius: 26, padding: '16px 10px', gap: 16 }}>
        <div className="flex items-center" style={{ gap: 10, padding: '2px 8px' }}>
          <img src="/app-icon-192.png" alt="" style={{ width: 32, height: 32, borderRadius: 9 }} />
          <div className="flex flex-col flex-1 min-w-0">
            <span style={{ fontSize: 14, fontWeight: 600 }}>ImmoSchwede</span>
            <span style={{ fontSize: 11, color: 'var(--ink3)' }}>Verwaltung</span>
          </div>
          {isDemoMode && <Chip ton="warn" groesse="xs">DEMO</Chip>}
          <button type="button" aria-label="Menü schließen" onClick={onClose} className="lg:hidden flex items-center justify-center cursor-pointer"
            style={{ width: 30, height: 30, borderRadius: 10, border: 'none', background: 'var(--fill2)', color: 'var(--ink)' }}>
            <X weight="bold" size={14} />
          </button>
        </div>

        {ort ? (
          <ObjektModus
            ort={ort}
            zustand={zustand}
            panel={suchParameter.get('panel')}
            haus={suchParameter.get('haus')}
            onNavigate={onNavigate}
          />
        ) : mieterId ? (
          <MieterModus mieterId={mieterId} zustand={mieterZustand} panel={suchParameter.get('panel')} onNavigate={onNavigate} />
        ) : eigentuemerId ? (
          <EigentuemerModus eigentuemerId={eigentuemerId} zustand={eigentuemerZustand} panel={suchParameter.get('panel')} onNavigate={onNavigate} />
        ) : dienstleisterId ? (
          <DienstleisterModus dienstleisterId={dienstleisterId} zustand={dienstleisterZustand} panel={suchParameter.get('panel')} onNavigate={onNavigate} />
        ) : kontaktId ? (
          <KontaktModus kontaktId={kontaktId} zustand={kontakt.zustand} anzahl={kontakt.anzahl} tab={kontaktTab(suchParameter.get('tab'))} onNavigate={onNavigate} />
        ) : emailBereich ? (
          <>
            <EmailModusKopf />
            <EmailModus userRole={userRole} allowedModules={allowedModules} onNavigate={onNavigate} />
          </>
        ) : (
          <>
            <ListenModus
              aktiveId={routeItemId(location.pathname)}
              sichtbarkeit={sichtbarkeit}
              badges={{ rechnungenBadge, emailBadge, vorschlaegeBadge, unreadByModul }}
              laedt={loading}
              onNavigate={onNavigate}
              onSuche={openSearch}
            />
            <Nutzerkarte
              name={fullName}
              email={userEmail}
              rolle={rollenLabel(userRole)}
              avatarUrl={avatarUrl}
              onProfil={() => onNavigate('/admin/profile')}
              aktiv={location.pathname === '/admin/profile'}
              onAbmelden={onLogout}
            />
          </>
        )}
      </div>
    </aside>
  );
}
